/**
 * English - `tipsTheme`: the ⓘ explanations beside theme editor fields (theme
 * settings, the page picker, reusable sections and the component inspector).
 * One key per field, `<form>.<field>`, passed as the field's `info`.
 * Component options are keyed `part_option.<component type>.<option key>`.
 * Conventions: see `common.ts`.
 */
const tipsTheme: Record<string, string> = {
  // >>> customizer
  'customizer.page':
    'The storefront page you are arranging right now. Pick another page to edit its sections; the preview switches with it. Pages marked as changed already have your own layout.',
  'customizer.theme_name':
    'A name for this theme, only seen by you and your team in the theme list. Customers never see it. Example: Diwali Sale 2026',
  'customizer.mode':
    'Simple shows the words, pictures and size of each section, with the rest under More options. All settings opens everything at once. Only changes this editor, never your store.',
  'builder.reusable_name':
    'A name for this saved section so you can find it again under Add section on any page of your shop. Use at least 2 characters. Example: Free delivery strip',

  // >>> style panel
  'style.logo':
    'Your logo shows at the top of every page, and the small icon (favicon) shows in the browser tab. Both belong to your shop, so they stay when you change theme.',
  'style.palette':
    'A colour set changes your main colour, background and text together, so they always go well with each other. Pick Custom to choose each colour yourself.',
  'style.fonts':
    'Each choice sets one font for headings and one for the rest of your text. The line under each name shows how your shop will read.',
  'style.buttons':
    'How the corners of your buttons look, and whether they are filled with your main colour or only outlined. Every button in your shop changes together.',

  // >>> layout
  'layout.family':
    'The overall look of your storefront: how the header, footer, product pages and listings are drawn. Your products, pages and words stay the same when you switch.',
  'layout.area.header':
    'Use a different theme style for the header only. Leave it on Match theme style to keep it in step with the rest of the shop.',
  'layout.area.footer':
    'Use a different theme style for the footer only. Leave it on Match theme style to keep it in step with the rest of the shop.',
  'layout.area.product':
    'Use a different theme style for product pages only. Leave it on Match theme style to keep it in step with the rest of the shop.',
  'layout.area.listing':
    'Use a different theme style for product listing and category pages only. Leave it on Match theme style to keep it in step with the rest of the shop.',

  // >>> logo and favicon
  'logo.image':
    'Your brand mark, shown in the header on every page. It belongs to your store, so it stays when you switch themes. A PNG with a transparent background looks best.',
  'logo.width':
    'How big the logo is drawn in the header. Medium suits most logos; pick Large for a wide wordmark.',
  'logo.show_store_name':
    'When on, your store name is written beside the logo. Turn it off if your logo already contains the name, so it does not appear twice.',
  'logo.sticky':
    'When on, the header stays pinned to the top while customers scroll, so the menu and cart are always in reach. When off, it scrolls away with the page.',
  'logo.favicon':
    'The small icon shown in the browser tab and bookmarks. Use a simple square image, 32 pixels or larger, such as your logo mark on its own.',
  'logo.adjust_shape':
    'Square suits a logo mark and is what the installed app icon uses; 3:1 suits a wide wordmark in the header.',
  'logo.adjust_zoom':
    'Enlarges the picture inside the frame, so a small mark fills the space. Arrow keys on the frame move it; Shift moves it further.',
  'logo.adjust_trim':
    'Finds where your logo actually is - ignoring a transparent or plain-colour border - and frames it with a little breathing room.',

  // >>> announcement bar
  'banner.message':
    'The short line shown in the bar above the header on every page. Up to 160 characters. Example: Free shipping on orders over ₹999',
  'banner.link_label':
    'Optional words for a link at the end of the message. Up to 40 characters. Example: Shop now',
  'banner.href':
    'Where the link takes customers: a product, a category, one of your pages, WhatsApp, or another website.',
  'banner.autoplay':
    'When on, the bar moves to the next banner by itself. When off, customers use the dots to switch between banners.',
  'banner.interval':
    'How long each banner stays on screen before the next one shows, in seconds. Choose between 3 and 15. Example: 5',

  // >>> area appearance (header, announcement bar, footer)
  'appearance.background':
    'The colour behind this area. Theme default keeps the theme look; a palette colour follows your palette when you change it. Example: Brand colour',
  'appearance.text':
    'The colour of words and icons in this area. Theme default keeps the theme text colour. A warning shows if it is hard to read on the background.',
  'appearance.divider':
    'A line under this area, in pixels. 0 or Auto shows no extra line. Choose between 0 and 4. Example: 1',
  'appearance.divider_color':
    'The colour of the line under this area. Theme default uses the soft tint of the area.',
  'appearance.width':
    'Full runs the bar colour to the edges of the screen. Page keeps it within the page column, like the rest of your content.',
  'appearance.padding_top':
    'Space above the message, in pixels. Auto uses the theme spacing. Choose between 0 and 60. Example: 8',
  'appearance.padding_bottom':
    'Space below the message, in pixels. Auto uses the theme spacing. Choose between 0 and 60. Example: 8',
  'footer.space_above':
    'Room added above the footer, in pixels, on top of the theme spacing. Choose between 0 and 120. Example: 24',
  'footer.space_below':
    'Room added below the footer, in pixels, on top of the theme spacing. Choose between 0 and 120. Example: 24',
  'footer.copyright_size':
    'How big the copyright line at the bottom of the footer is, in pixels. Auto uses the theme size. Choose between 10 and 18. Example: 12',
  'footer.copyright_uppercase':
    'Shows the copyright line at the bottom of the footer in capital letters.',
  'footer.layout':
    'The theme\'s own keeps the footer your theme was designed with. Three columns shows your shop name and description, your footer menu, and your help pages side by side. Large adds up to four link columns, an optional background photo and a separate bottom bar with your copyright, social links and payment options. Both work in any theme.',
  'footer.show_social':
    'When on, the three-column footer shows round icons linking to your social profiles under your shop name, and the large footer shows them in its bottom bar.',
  'footer.social_url':
    'The web address of your profile. For WhatsApp, your phone number with the country code is enough. Example: https://www.instagram.com/yourshop',
  'header.location_selector':
    'Shows a "Deliver to" button in the header, as grocery and quick-delivery shops do. Customers type their 6-digit PIN code and see straight away whether you deliver there. The PIN code is remembered and also fills the delivery check on product pages.',
  'header.category_icons':
    'Puts a small round picture of each category before its name in the category links of your header. Uses the picture of the category, or its first letter when it has none.',
  'header.layout':
    'The theme\'s own keeps the header your theme was designed with. Centred logo puts your logo in the middle, your menu on the left and search, account and cart on the right, in any theme. Menu items that do not fit go under More.',
  'footer.large_style':
    'Standard is the big footer as it is. Framed puts the benefits ribbon inside as a rounded box, with bold headings, your logo and an email signup last, and a rounded bottom bar. With contact details shows your logo beside your name with social links under it, adds a Contact us column (address, phone, email, hours) from your first store location, and puts your policy links in the bottom bar.',
  'footer.newsletter_heading':
    'The heading above the email box in the framed footer. Example: Get wellness tips and offers. Leave empty to show no signup.',
  'footer.newsletter_text':
    'One or two sentences under the signup heading saying what people get. Example: Be first to hear about new arrivals and offers.',
  'footer.background_image':
    'A photo behind the large footer. It is darkened so your links stay easy to read. Leave it empty for a plain footer. Example: a wide photo of your shop or products',
  'footer.overlay':
    'How much the photo is darkened behind the words, from 0 (not at all) to 80 (nearly black). Example: 60',
  'footer.ribbon_enabled':
    'When on, a strip of short benefits scrolls sideways just above the footer on every page. It stands still for shoppers who ask their phone for less motion.',
  'footer.directory':
    'A dense block of small grouped links just above the footer, like a marketplace’s list of categories and cities. Your categories lists each main category with its sub-categories; My own groups lets you type headings and links, such as a region with the cities you deliver to.',
  'footer.directory_title':
    'An optional heading over the link directory, up to 80 characters. Leave it empty for no heading. Example: Popular categories',
  'footer.directory_groups':
    'Each group is a bold heading followed by a run of links to pages on your shop. Up to 24 groups.',
  'footer.play_store_url':
    'Your own app’s Google Play listing. A Get it on Google Play button appears in the footer’s last column. Must start with https://play.google.com/',
  'footer.app_store_url':
    'Your own app’s App Store listing. A Download on the App Store button appears in the footer’s last column. Must start with https://apps.apple.com/',
  'footer.ribbon_item_text':
    'A short benefit shown in the strip, up to 80 characters. Empty items are not shown. Example: Free delivery over ₹999',
  'footer.ribbon_icon':
    'A small picture shown before the words. Choose No icon to show only the words. Example: Truck',
  'footer.ribbon_speed':
    'How fast the strip moves. Slow is easier to read; fast suits very short items. Example: Normal',

  // >>> header menu and department bar
  'header_look.cta_label':
    'The words on a button shown at the end of your header, on every page and with every header style. Up to 30 characters. It shows only when it also has a link. Example: Book appointment',
  'header_look.cta_href':
    'Where the header button goes when a customer taps it, such as your booking page. Example: /pages/book',
  'header_menu.limit':
    'How many of your main categories appear in the header row when you have not built your own header menu. Pick a smaller number if the names do not fit on one line.',
  'header_menu.mega_menu':
    'How a category in your header opens. A small list shows its sub-categories under it. A big menu fills the width of the page with a column for each sub-category, and can add a picture that links to an offer. Works with every theme.',
  'header_menu.promo_heading':
    'The words under the picture in the big menu. Up to 60 characters. Example: New arrivals',
  'header_menu.promo_image':
    'The picture shown beside the columns. If you add none, each category shows its own picture.',
  'header_menu.promo_link':
    'Where the picture goes when a shopper taps it. Leave it empty to open the category.',
  'header_menu.card_heading':
    'The words centred under this card in the big menu. Up to 40 characters. Example: Best Selling',
  'header_menu.card_image':
    'The large rounded picture on this card. If you add none, a sub-category’s own picture is used.',
  'header_menu.card_link':
    'Where the card goes when a shopper taps it. Leave it empty to open the category - the second card shows its newest products.',
  'department_bar.menu_label':
    'The words on the button that opens the full list of departments. Up to 40 characters. Example: All departments',
  'department_bar.tagline':
    'A short line at the end of the department bar, such as your hours or a promise. Up to 60 characters. Leave it empty to hide it. Example: Open Mon-Sat, 9 am to 7 pm',

  // >>> language selector
  'language_switcher.show':
    'When on, customers see a selector to read your shop in another language. It only appears if your store has more than one language turned on.',
  'language_switcher.placements':
    'Where the language selector is shown. Tick one or more places: the header, the footer, or the menu on phones.',
  'language_switcher.display':
    'How each language is named in the selector: in its own script (हिन्दी), in English (Hindi), as a short code (HI), or both name and code.',

  // >>> colours
  'colors.primary':
    'Your main brand colour, used for buttons, links and prices across the shop. Pick one that stands out against the page background.',
  'colors.background':
    'The colour behind every page. Most shops use white or a very light shade so products stand out.',
  'colors.surface':
    'A soft shade for cards, image placeholders and tinted sections. Keep it close to the page background so it reads as a gentle panel.',
  'colors.text':
    'The colour of headings and body text. Choose a dark shade on a light background so it is easy to read.',
  'colors.muted':
    'A lighter colour for captions, small print and supporting lines. It should still be readable against the background.',

  // >>> type
  'type.font':
    'The typeface for all body text, and for headings too unless you pick a heading font. Example: Inter',
  'type.heading_font':
    'An optional typeface just for headings. Choose Same as body to use one font everywhere. Example: Playfair Display',
  'type.heading_weight':
    'How thick heading letters are. Bold and Extra bold feel strong and loud; Medium feels lighter and calmer.',
  'type.base_size':
    'The size of normal body text, in pixels. Headings scale with it. Choose between 12 and 20. Example: 16',

  // >>> buttons
  'buttons.style':
    'How buttons look across the shop. Filled buttons are solid blocks of your brand colour; Outlined buttons show only a border.',
  'buttons.radius':
    'How rounded the corners of buttons are, in pixels. Cards and images follow it too. 0 gives sharp corners. Choose between 0 and 32. Example: 8',

  // >>> component inspector, shared controls
  'part.words':
    'The words this component shows, such as a heading or a button label. Leave it blank to keep the wording your shop already uses here.',
  'part.text':
    'The paragraph this component shows. Press Enter for a new line. Example: Every order is packed by hand in Jaipur.',
  'part.stands_in':
    'Which area of the page this component sits in. Each theme draws its areas in its own way, so check the preview after moving it.',
  'part.show_on_storefront':
    'When on, customers see this component. When off, it is hidden but keeps its settings, so you can bring it back later.',
  'part.space_above':
    'How much empty space is left above this component. The theme\'s own keeps the spacing the theme was designed with.',
  'part.space_below':
    'How much empty space is left below this component. The theme\'s own keeps the spacing the theme was designed with.',
  'part.alignment':
    'Whether this component lines up to the left, centre or right of its area.',
  'part.width':
    'Whether this component is only as wide as its content or stretches across the whole column.',
  'part.hide_on':
    'Tick the screen sizes where this component should not appear. Useful for keeping phone pages short. Leave all unticked to show it everywhere.',
  'part.text_size':
    'How big the text of this component is. The theme\'s own keeps the size the theme chose.',
  'part.text_weight':
    'How thick the letters are, from Normal to Bold.',
  'part.text_case':
    'Whether the words show as you typed them, in ALL CAPITALS, or with Each Word Capitalised.',
  'part.color':
    'The text colour. Picking one of your shop\'s palette colours keeps this in step if you change your palette later. The last swatch picks any other shade.',
  'part.color_custom':
    'The exact colour for this component\'s text. Pick it from the palette or type a hex code. Example: #1E3A8A',
  'part.background':
    'A panel behind this component: a palette colour or one of your own. A background also adds some padding around it, which you can set exactly below.',
  'part.padding_top':
    'Exact space inside this component above its content, in pixels. Auto keeps the theme spacing. Choose between 0 and 120. Example: 16',
  'part.padding_bottom':
    'Exact space inside this component below its content, in pixels. Auto keeps the theme spacing. Choose between 0 and 120. Example: 16',
  'part.padding_left':
    'Exact space inside this component on its left, in pixels. Auto keeps the theme spacing. Choose between 0 and 120. Example: 16',
  'part.padding_right':
    'Exact space inside this component on its right, in pixels. Auto keeps the theme spacing. Choose between 0 and 120. Example: 16',

  // >>> component options: custom components
  'part_option.custom_heading.level':
    'How the heading is marked for search engines and screen readers. Use Section heading for a main title, Sub-heading under it, or Not a heading for large text that is only decorative.',
  'part_option.custom_button.href':
    'The page the button opens: a page on your store or a full web address. Example: /pages/size-guide',
  'part_option.custom_button.style':
    'How this button looks: filled with your brand colour, outlined with a border, or as plain underlined text.',
  'part_option.custom_button.new_tab':
    'When on, the link opens in a new browser tab so customers keep your shop open. Best for links to other websites.',
  'part_option.custom_image.image_url':
    'The picture to show, such as a size chart or care label. Upload one, pick from your media library or make one with AI.',
  'part_option.custom_image.alt':
    'A short description of the picture for customers using screen readers, and for search engines. Example: Size chart for men\'s kurtas',
  'part_option.custom_image.href':
    'Optional page the picture opens when tapped: a page on your store or a full web address. Example: /pages/size-guide',
  'part_option.custom_image.max_width':
    'The widest the picture may grow, in pixels. 0 lets it fill the column. Choose between 0 and 1200. Example: 600',
  'part_option.spacer.height':
    'How much empty space to add, in pixels. Choose between 4 and 200. Example: 32',

  // >>> component options: product page
  'part_option.product_breadcrumbs.home_label':
    'The first link in the trail at the top of the product page, which takes customers back to your home page. Example: Home',
  'part_option.product_breadcrumbs.catalog_label':
    'The second link in the trail, which opens your full product list. Leave it blank to skip it. Example: All products',
  'part_option.product_breadcrumbs.show_current':
    'When on, the trail ends with the name of the product being viewed.',
  'part_option.product_gallery.aspect':
    'The shape product pictures are shown in: square, portrait (tall) or wide. The theme\'s own keeps the theme\'s shape.',
  'part_option.product_gallery.thumbs':
    'Where the small preview pictures sit: under the main picture, or in a strip beside it.',
  'part_option.product_gallery.style':
    'How product pictures are laid out: one large picture with a strip of small ones, a rail of small pictures down the left, small pictures underneath, or every picture at once, two to a row.',
  'part_option.product_gallery.sticky':
    'When on, the pictures stay on screen while customers scroll down the details beside them. Works on computer screens only.',
  'part_option.product_gallery.rounded':
    'When on, product pictures have softly rounded corners. When off, the corners are square.',
  'part_option.product_price.show_compare_at':
    'When on, the original price is shown struck through beside the selling price whenever the product has one. Example: ₹1,299 beside ₹999',
  'part_option.product_price.show_saving':
    'When on, a note such as "Save 20%" shows beside the price whenever the product has an original price to compare with.',
  'part_option.product_compare_price.show_label':
    'When on, the word from the Words field is shown before the original price. Example: MRP ₹1,299',
  'part_option.product_availability.in_stock_text':
    'What customers read when the product can be bought. Leave it blank to use the shop\'s usual wording. Example: Ready to ship',
  'part_option.product_availability.out_of_stock_text':
    'What customers read when the product is sold out. Leave it blank to use the shop\'s usual wording. Example: Back soon',
  'part_option.product_availability.show_dot':
    'When on, a small coloured dot shows beside the stock message: green when in stock, grey when not.',
  'part_option.product_highlights.style':
    'How the highlight lines are shown: as small outlined chips, as a list with a tick before each line, or as icons over small labels. For icons, start a line with an icon name and a colon - leaf, droplet, flask, heart, sprout, sun, award, shield, check, star, wheat, recycle, flame or snowflake. Example: leaf: Organic',
  'part_option.product_variants.style':
    'How customers choose size, colour and other options: one button per combination, one row per option, colour dots for colours, or a dropdown.',
  'part_option.product_variants.hide_sold_out':
    'When on, sold-out choices are left out so customers only see what they can buy. When off, every choice is listed.',
  'part_option.product_quantity.max':
    'The largest quantity a customer can choose in one go. Choose between 1 and 99. Example: 10',
  'part_option.product_buy_now.style':
    'How the Buy now button looks: filled with your brand colour, or outlined with a border.',
  'part_option.product_buy_on_whatsapp.show_icon':
    'When on, the WhatsApp logo is shown on the button so customers know it opens a chat.',
  'part_option.product_description.show_heading':
    'When on, a heading appears above the product description. The heading uses the text in the Words field.',
  'part_option.product_description.collapsible':
    'When on, the description is folded away and opens when customers tap it. Handy for long descriptions.',
  'part_option.product_shipping_info.collapsible':
    'When on, the delivery and returns details are folded away and open when customers tap them.',
  'part_option.product_shipping_info.icon':
    'A small picture shown beside the delivery and returns heading, such as a van or a returns arrow. Pick None for no picture.',
  'part_option.product_payment.methods':
    'The payment methods you accept, separated by commas. Each one shows as a small label. Example: UPI, Visa, Mastercard, RuPay, Cash on delivery',
  'part_option.product_payment.collapsible':
    'When on, the payment and security details are folded away and open when customers tap them.',
  'part_option.product_share.facebook':
    'When on, customers see a button to share this product on Facebook.',
  'part_option.product_share.x':
    'When on, customers see a button to share this product on X (formerly Twitter).',
  'part_option.product_share.whatsapp':
    'When on, customers see a button to send this product to a friend on WhatsApp.',
  'part_option.product_share.copy_link':
    'When on, customers see a button that copies the product\'s link so they can paste it anywhere.',
  'part_option.product_comparison.columns':
    'How many products appear side by side in the comparison table, counting this one. Choose between 2 and 4. Example: 3',
  'part_option.product_comparison.show_description':
    'When on, the table has a row with each product\'s short summary.',
  'part_option.product_comparison.show_options':
    'When on, the table has a row listing each product\'s options, such as sizes or colours.',
  'part_option.product_comparison.show_brand':
    'When on, the table has a row with each product\'s brand.',
  'part_option.product_comparison.show_availability':
    'When on, the table has a row showing whether each product is in stock.',
  'part_option.product_accordion.include_specs':
    'When on, each of the product\'s specifications with a value becomes a panel, before the panels you write yourself.',
  'part_option.product_accordion.first_open':
    'When on, the first panel is open when the page loads.',
  'part_option.product_accordion.single_open':
    'When on, opening a panel closes the one that was open. When off, customers can open several at once.',
  'part_option.product_delivery_estimate.show_cod':
    'When on, the answer also says whether customers at that PIN code can pay cash on delivery.',
  'part_option.product_delivery_estimate.min_days':
    'The fewest days an order usually takes to reach a customer, counting packing and travel. Used only when your delivery settings give no date. Choose between 1 and 30. Example: 3',
  'part_option.product_delivery_estimate.max_days':
    'The most days an order usually takes to reach a customer. Shown as "Usually delivered in 3-7 days". Choose between 1 and 45. Example: 7',
  'part_option.product_stock_bar.threshold':
    'The bar appears once this many pieces or fewer are left. Products whose stock you do not track never show it. Choose between 1 and 50. Example: 10',
  'part_option.product_stock_bar.style':
    'How low stock is shown: the words "Only 4 left" over a bar that empties as stock runs out, or the words on their own.',
  'part_option.product_offers.show_copy':
    'When on, each code has a Copy button so customers can paste it at checkout. Write one offer per line in the Text field, as CODE: what it gives. Example: SAVE10: 10% off your first order',
  'part_option.product_sticky_cart.show_on':
    'Where the bar appears once the main Add to cart button scrolls out of view: on phones only, or on every screen size.',
  'part_option.product_sticky_cart.show_image':
    'When on, the bar shows a small copy of the product\'s first picture beside its name.',
  'part_option.product_size_chart.image_url':
    'A picture of your size chart, shown in the pop-up above the table. Leave it empty to show the table only.',
  'part_option.product_size_chart.note':
    'A short line shown under the table, such as how the measurements were taken. Example: Measurements are of the garment, in inches.',
  'part_option.product_bundle.show_checkboxes':
    'When on, each item has a tick box so customers can leave some out before adding the rest. The bundle discount you set on the product applies only when every item is in the cart.',
  'part_option.product_bundle.layout':
    'Pictures in a row joins the product pictures with + signs. A list shows one item per line with its price - better for long product names.',
  'part_option.product_qa.page_size':
    'How many questions show before the "Show more questions" button. Only questions you have published appear. Example: 5.',
  'part_option.product_qa.allow_questions':
    'When on, customers can send you a question from the product page. New questions wait in Questions (under Products) until you answer and publish them.',
  'part_option.product_qa.show_ask_button':
    'When on, the question form opens only after a customer taps "Ask a question". Turn it off to show the form open under the questions.',
  'part_option.product_notify_me.hide_add_to_cart':
    'When on, the add-to-cart button is hidden while the chosen option is sold out, and only the email box shows. Turn it off to keep the greyed-out button above the box.',

  // >>> component options: cart and checkout
  'part_option.cart_items.show_image':
    'When on, each item in the cart shows a small picture of the product.',
  'part_option.cart_items.show_options':
    'When on, the chosen size, colour or other option is shown under each product name. Example: Size M / Blue',
  'part_option.cart_items.allow_quantity':
    'When on, customers can change how many of each item they want right in the cart. When off, the quantity is fixed there.',
  'part_option.cart_items.allow_remove':
    'When on, each item has a button to take it out of the cart.',
  'part_option.cart_coupon.button_label':
    'The words on the button customers press to apply their discount code. Leave it blank for the usual wording. Example: Apply',
  'part_option.cart_note.placeholder':
    'The faint hint shown inside the order notes box before the customer types. Leave it blank for the usual wording. Example: Leave it with the guard',
  'part_option.cart_note.max_length':
    'The most characters a customer may write in their order notes. The note reaches your order as the customer note. Example: 500',
  'part_option.checkout_summary_items.show_image':
    'When on, each item in the checkout order summary shows a small picture of the product.',
  'part_option.listing_filters.mode':
    'What customers can narrow your product list by. Categories only shows your categories as links. The full filters add price, brand, product type, size, colour and in-stock, each with a count, inside the category being browsed; on a phone they open from a Filters button. Example: Price, brand, size, colour and stock',
  'part_option.listing_filters.sidebar_style':
    'How each category looks when the categories stand in a sidebar. Picture and name puts a small rounded picture of the category beside its name and tints the one being browsed; categories without a picture show their first letter. Words only keeps plain links. Example: Picture and name',
  'part_option.listing_banner.layout':
    'How the banner is laid out. Picture beside the words splits it in two; words over the picture lays them on the photo with a dark wash so they stay readable; words only drops the picture. A banner without any picture always shows words only.',
  'part_option.listing_banner.height':
    'How tall the picture part of the banner is. Phones get a slightly shorter banner.',
  'part_option.listing_banner.image_side':
    'Which side the picture stands on when it is beside the words. On a phone the picture always comes first, above the words.',
  'part_option.listing_banner.image_url':
    'A picture used instead of the category\'s own. Leave it empty to show each category\'s picture. On the full product list, which has no category, this is the only picture the banner can show.',
  'part_option.listing_promo_tile.position':
    'Where the tile stands among the products, counted from the first one. It shows on the first page only, and not at all when that page has fewer products. Example: 4 puts it in place of the fourth product.',
  'part_option.listing_promo_tile.span':
    'How many product spaces the tile takes. Two wide makes it stand out more; on a phone it then fills the whole row.',
  'part_option.listing_promo_tile.image_url':
    'The picture behind the tile\'s words. Leave it empty for a plain tile in the colour chosen below.',
  'part_option.listing_promo_tile.color':
    'The tile\'s colour when it has no picture, taken from your shop\'s colours so it changes with them.',
  'part_option.listing_promo_tile.eyebrow':
    'A short line in small capitals above the heading. Example: Limited time',
  'part_option.listing_promo_tile.heading':
    'The tile\'s main words. Leave it blank to show "New arrivals", translated into the shopper\'s language. Example: Festive sale - up to 30% off',
  'part_option.listing_promo_tile.text':
    'A short line under the heading. Shown on the small offer card when the tile stands at the top of the page, under the category heading. Example: On your first order',
  'part_option.listing_promo_tile.cta_label':
    'The words on the tile\'s link. Shown only when the tile links somewhere; blank shows "Shop now". Example: See the offer',
  'part_option.listing_promo_tile.href':
    'The page on your shop the tile opens, starting with /. Addresses on other websites are not allowed. Example: /categories/sarees',

  // >>> round 3: history, share, schedule, kits
  'history.panel':
    'Every publish, restore, festival switch and AI change keeps a copy of your theme, plus one every few minutes while you edit. Preview only shows it; Restore puts it back, and you can undo that too.',
  'share.link':
    'Anyone who opens this link sees this draft on your real shop for 7 days, without signing in. They cannot change anything. Stop sharing turns the link off at once.',
  'schedule.start_date':
    'The day this theme becomes your live shop. Example: 20 Oct for Diwali.',
  'schedule.start_time':
    'The time on that day, in India time (IST). 12:00 am is the very start of the day.',
  'schedule.with_end':
    'Turn on to put your current live look back automatically on a later date, so the festival look comes down by itself.',
  'schedule.end_date':
    'The day your current look comes back. Example: 3 Nov, after Diwali week.',
  'schedule.end_time':
    'The time your current look comes back on that day, in India time (IST).',
  'kits.with_products':
    'Adds a row of products under the offer strip, with a festive heading. Turn off to add only the colours, banner words and offer strip.',
  'kits.category':
    'Which products the row shows. Pick a category such as Gifts, or All products to show your newest items.',
  // >>> plugins
  'plugins.quick_cart.enabled':
    'Clicking the cart icon opens a cart that slides in from the right instead of going to the cart page. Some themes, like Kinetic, have it on by default.',
  'plugins.quick_cart.open_on_add':
    'After a shopper adds a product, the cart slides open so they can see it was added. Turn this off to send them to the cart page instead.',
  'plugins.quick_cart.free_shipping_bar':
    'Shows how much more the shopper needs to spend for free delivery. Set the amount under your shipping settings.',
  'plugins.quick_cart.recommendations':
    'Shows a row of related products under the cart items, to help shoppers add one more thing.',
  'plugins.promo_popup.enabled':
    'Shows a popup with your offer and an email sign-up form. Shoppers who sign up are added to your customers as subscribed to marketing.',
  'plugins.promo_popup.show_on':
    'The pages where the popup can appear. It shows at most once per visit, wherever the shopper sees it first.',
  'plugins.promo_popup.delay_seconds':
    'How long a shopper is on the page before the popup appears. A few seconds lets them see your shop first.',
  'plugins.promo_popup.eyebrow': 'A short line above the headline, like "Limited time offer".',
  'plugins.promo_popup.headline': 'The big words of your offer, like "Up to 50% off".',
  'plugins.promo_popup.subtitle': 'One sentence that tells shoppers why to sign up.',
  'plugins.promo_popup.button_label': 'The text on the sign-up button.',
  'plugins.promo_popup.image_url':
    'A tall picture shown beside your offer. On phones it is hidden, so the offer has room.',
  'plugins.promo_popup.panel_color':
    'The colour behind your offer. The text turns light or dark on its own so it stays easy to read.',
  'plugins.promo_popup.collect_name':
    'Adds a name field above the email field. Turn it off to ask for the email address only.',
  'plugins.search_suggest.enabled':
    'Turn on to show results while shoppers type in search. Turn off and the search icon simply opens the search page.',
  'plugins.search_suggest.style':
    'Full screen covers the page with a large search box. Dropdown opens a smaller box under the search icon.',
  'plugins.search_suggest.show_products':
    'Shows matching products with their picture, name and price.',
  'plugins.search_suggest.show_categories':
    'Shows matching categories so shoppers can jump straight to a whole shelf.',
  'plugins.search_suggest.max_results':
    'How many products to suggest at once, from 3 to 10. Pressing Enter always shows every result.',
  'plugins.wishlist.enabled':
    'Turn on to let shoppers save products with a heart. Guests keep their list on their device; signed-in shoppers keep it in their account.',
  'plugins.wishlist.show_in_header':
    'Adds a heart next to the cart showing how many products are saved. Tapping it opens the wishlist page.',
  'plugins.wishlist.show_on_cards':
    'Puts a small heart on every product picture in your grids, so shoppers can save without opening the product.',
  'plugins.wishlist.show_on_product':
    'Puts a heart next to the Add to cart button on each product page.',
  'plugins.color_mode.enabled': "Turn on to offer your shop in light and dark. A light theme gets a dark mode and a dark theme a light one, worked out from your colours. Each shopper's choice stays on their device.",
  'plugins.color_mode.default_mode': "Your theme's own colours shows the shop as you designed it until a shopper switches. The device setting follows whatever their phone or computer is set to.",
  'plugins.color_mode.show_toggle': 'Adds a sun or moon button next to the cart. Turn off to follow the device setting only, with no button.',
  'plugins.color_mode.alternate_primary': 'The brand colour for buttons and links in the other mode. Empty keeps your brand colour, adjusted so it stays readable.',
  'plugins.color_mode.alternate_background': 'The page colour in the other mode. Empty picks a near-black (or near-white) with a hint of your own background.',
  'plugins.color_mode.alternate_surface': 'The colour of cards, panels and boxes in the other mode. Empty keeps the same step from the page as in your theme.',
  'plugins.color_mode.alternate_text': 'The main text colour in the other mode. Empty picks one with strong contrast against the page.',
  'plugins.color_mode.alternate_muted': 'Secondary text such as hints and dates in the other mode. Empty picks a readable grey.',
  'plugins.app_tab_bar.enabled': 'Turn on to give phone shoppers a bar fixed to the bottom of the screen, so your shop feels like an app. It stays put while they scroll, and is hidden on tablets and computers.',
  'plugins.app_tab_bar.show_labels': 'Shows a short name such as Home or Cart under each icon. Turn off for icons only.',
  'cards.hover_image':
    "When a shopper points at a card, it fades to the product's second photo. Products with one photo stay as they are.",
  'cards.badge_sale': 'Marks products whose compare-at price is higher than their price.',
  'cards.badge_new': 'Marks products you added within the number of days set below.',
  'cards.badge_new_days': 'How long a product counts as new after you add it: 7 to 90 days.',
  'cards.badge_bestseller': 'Marks your ten best-selling products by units sold. Shops with no sales yet show none.',
  'cards.badge_sold_out': 'Puts a sold out badge on the photo of products with no stock left. It replaces the other badges.',
  'cards.badge_style': 'The shape every badge takes: a rounded pill, a ribbon from the edge, or a tab in the corner.',
  'cards.swatches': "Shows each product's colours as small dots under the price - up to five, then '+2' for the rest. Colours we cannot paint show as their name.",
  'cards.quick_add': 'Lets shoppers add to cart from the card. A product with one option goes straight in; one with several opens a small picker. The quick cart opens if it is set to open on add.',
};

export default tipsTheme;
